import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { readContracts, simulateContract, waitForTransactionReceipt, writeContract, WriteContractErrorType } from "@wagmi/core";
import { BehaviorSubject } from "rxjs";
import { Address, formatEther, parseEther, parseUnits, WriteContractReturnType } from "viem";

import { environment } from "../../../environments/environment";
import { getParam, SwapStatus } from "../../shared/constants/app-enums";
import { checkIsValidReferral, getChainData, getPresaleContract, getUSDCContract, getUSDTContract, presaleContractEth } from "../../shared/constants/wallet-connect.const";
import { AnalyticsService } from "../analytics.service";
import { PresaleDataService } from "../connection-services/data.service";
import { PopupService } from "../popup.service";


@Injectable({
    providedIn: 'root',
})


export class EvmPurchseService {
    $transactionUpdates = new BehaviorSubject<{ status: SwapStatus, hash?: string }>({ hash: "", status: SwapStatus.not_started });

    constructor(
        private dataService: PresaleDataService,
        private analyticsService: AnalyticsService,
        private popupService: PopupService,
        private transletService: TranslateService
    ) {
    }

    /**
   * this function is swaping the native token with app token
   * @param amount: app token amount
   * @param value: eth token value
   */

    async swapNativeTokens(amount: number, value: number, isBuyStake: boolean = false) {
        // setting swapStatus to not started
        this.$transactionUpdates.next({ status: SwapStatus.not_started })

        /**
         * preparing a contract for swaping via smart contract
         * method of ethBuyHelper
         */

        const referralAddress = getParam('referralAddress') || "0x0000000000000000000000000000000000000000";
        const isValidReferral = await checkIsValidReferral(referralAddress, this.dataService.chainId, this.dataService.walletAddress, this.dataService.wagmiConfig);
        const referral = isValidReferral ? referralAddress : "0x0000000000000000000000000000000000000000";

        try {
            const { request } = await simulateContract(this.dataService.wagmiConfig, {
                ...getPresaleContract(this.dataService.chainId),
                functionName: getChainData(this.dataService.chainId)?.nativeFunction,
                chainId: getChainData(this.dataService.chainId).chainIdInt,
                args: [amount, referral],
                value: parseEther(`${value}`),
                // account:      this.walletAddress as Address    // Optional param. Not required!
            });
            console.log('simulateContract request =', request);

            // setting swapStatus to confirmation pending
            this.$transactionUpdates.next({ status: SwapStatus.confirm_pending })
            this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });

            /**
             * Writing contract for swap process
             */
            await writeContract(this.dataService.wagmiConfig, request).then(
                (hash: string) => {
                    console.log('Native write Contract success', hash);
                    this.$transactionUpdates.next({ status: SwapStatus.in_progess, hash: hash })
                    this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                    this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.dataService.walletAddress, hash, this.dataService.balanceDataEVM, this.dataService.chainId, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT);
                    this.checkTransaction(hash, getChainData(this.dataService.chainId).purchaseToken, amount, value, isBuyStake ? 'buy_and_stake' : 'buy', this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bnb');
                },
                (err: any) => {
                    console.log({ err }, "---err");
                    if (err.code === 4001) {
                        this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                        this.vwoData('failedAndRejectTransaction', {
                            transactionStatus: 'rejected',
                            fundSpentAmount: value,
                            tokenAmountPurchase: amount,
                            purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                            tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                            transactionId: '',
                            walletAddress: this.dataService.walletAddress,
                            walletType: this.dataService.connectedWalletName,
                        });
                        this.$transactionUpdates.next({ status: SwapStatus.rejected })
                    } else {
                        this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'failed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                        this.vwoData('failedAndRejectTransaction', {
                            transactionStatus: 'failed',
                            fundSpentAmount: value,
                            tokenAmountPurchase: amount,
                            purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                            tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                            transactionId: '',
                            walletAddress: this.dataService.walletAddress,
                            walletType: this.dataService.connectedWalletName,
                        });
                        this.$transactionUpdates.next({ status: SwapStatus.failed })
                    }
                },
            );
        } catch (error: any) {
            window.alert(error)
            console.error('SIMULATE FAILED ERROR: ' + error?.message || error.toString());
        }
    }

    /**
  * this function is check the final
  * status of the current transaction
  * @param hashKey: transaction hash key
  * @param mode: Eth/Bnb/Poly/Usdt transaction
  * @param amount: no of tooken
  * @param value:  amount of eth/bnb/usdt
  */

    async checkTransaction(hashKey: string, mode: string, amount: number, value: number, purchaseType: string, crypto: 'eth' | 'eth_usdt' | 'eth_usdc' | 'bnb' | 'bnb_usdt' | 'bnb_usdc' | 'usd' | '') {
        console.log('checkTransaction called');
        await waitForTransactionReceipt(this.dataService.wagmiConfig, { hash: hashKey as Address }).then(
            (response: { transactionHash: string }) => {
                console.log('checkTansaction success', response);
                clearTimeout(this.dataService.refreshId);
                this.dataService.getPresalesData()
                this.$transactionUpdates.next({ status: SwapStatus.complete, hash: response.transactionHash })
                this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'completed', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: response.transactionHash });
                this.vwoData('successfulTransaction', {
                    walletAddress: this.dataService.walletAddress,
                    transactionId: response.transactionHash,
                    mode: mode === 'usd' ? 'WERT' : 'CARYPTO',
                    walletName: this.dataService.connectedWalletName,
                    purchaseType,
                    tokenAmountPurchase: amount,
                    amountSpent: value,
                    cryptoType: crypto,
                    usdAmount: mode === 'usdt' || mode === 'usdc' ? +value : +(+(amount * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2)),
                });
                // this.swapHash = response.transactionHash; // assigning succes transaction hash id
                this.analyticsService.sendBWData(mode, amount, value, this.dataService.walletAddress, response.transactionHash, this.dataService.chainId, mode, this.dataService.balanceDataEVM, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT);
                this.analyticsService.windowDataLayer('swap', 'swapSuccessful', 3, 1, 0, mode, environment.tokenName, value, amount, this.dataService.walletAddress, response.transactionHash, this.dataService.balanceDataEVM, this.dataService.chainId, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT);
                // this.analyticsService.bestWalletModal(this.mode);
            },
            (err: any) => {
                console.log('checkTansaction error', err);
                this.$transactionUpdates.next({ status: SwapStatus.failed })
                this.vwoData('failedAndRejectTransaction', {
                    transactionStatus: 'failed',
                    fundSpentAmount: value,
                    tokenAmountPurchase: amount,
                    purchaseType: purchaseType,
                    tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                    transactionId: hashKey,
                    walletAddress: this.dataService.walletAddress,
                    walletType: this.dataService.connectedWalletName,
                });
            },
        );
    }

    /**
  * this function is checking allowence
  * amount before swaping the usdt token with app token
  * @param amount: app token amount
  * @param value: usdt token value
  * @param afterAllowance: checking whether allowence is granted or not
  */

    async swapCryptoForUSDT(amount: number, value: number, afterAllowance: boolean = false, isBuyStake: boolean = false, isUsdc: boolean = false) {
        /**
         * preparing token contract with ABI
         */
        const usdtContract = isUsdc ? getUSDCContract(this.dataService.chainId) : getUSDTContract(this.dataService.chainId);

        // set presale address based on current network
        const presaleAdd = getChainData(this.dataService.chainId).presaleAddress as Address;

        /**
         * reading allowence contract
         */

        const allowanceData = await readContracts(this.dataService.wagmiConfig, {
            contracts: [
                {
                    ...usdtContract,
                    functionName: 'allowance',
                    chainId: this.dataService.chainId,
                    args: [this.dataService.walletAddress, presaleAdd],
                },
            ],
        });


        let decimals = 6;
        let allowanceValue = Number(allowanceData[0].result) / Math.pow(10, 6);

        console.log('afterAllowance=', afterAllowance, 'allowance value =', allowanceValue, '\nrequest value =', value);

        /**
         * if inserted usdt value is larger than
         * provided allowence value then it needs further approval.
         * below we are preparing contract for allowence approval
         */

        if (+value > +allowanceValue) {
            const spendingCap = parseUnits(value.toString(), decimals);
            const { request } = await simulateContract(this.dataService.wagmiConfig, {
                ...usdtContract,
                chainId: this.dataService.chainId,
                functionName: 'approve',
                args: [presaleAdd, afterAllowance ? spendingCap : '0'],
                account: this.dataService.walletAddress as Address, // Optional param. Not required!
            });

            // setting swapStatus to approval pending;
            this.$transactionUpdates.next({ status: SwapStatus.approval_pending })
            this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });

            // fetching transaction hash key of writing contract
            const hash = await writeContract(this.dataService.wagmiConfig, request);
            console.log('approve allowence hash key =', hash);
            this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });

            /**
             * checking the approval transaction status.
             * if success and the allowence is preapproved
             * then it will call the buyUSDT function, else
             * will again go through the starting steps but this time with
             * allowence approval true mode.
             */
            await waitForTransactionReceipt(this.dataService.wagmiConfig, { hash }).then(
                (success) => {
                    console.log('approve Config success', success);
                    if (afterAllowance) {
                        this.buyUSDT(amount, value, isBuyStake, isUsdc);
                    } else {
                        this.swapCryptoForUSDT(amount, value, true, isBuyStake, isUsdc);
                    }
                },
                (err: any) => {
                    console.log('approve Config error', err);
                    this.$transactionUpdates.next({ status: SwapStatus.rejected })
                    this.vwoData('failedAndRejectTransaction', {
                        transactionStatus: 'rejected',
                        fundSpentAmount: value,
                        tokenAmountPurchase: amount,
                        purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                        tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                        transactionId: '',
                        walletAddress: this.dataService.walletAddress,
                        walletType: this.dataService.connectedWalletName,
                    });
                },
            );
        } else {
            /**
             * if inserted usdt value is smaller than
             * provided allowence value, then buyUSDT
             * function will be called and doesn't need
             * to check for allowence approval
             */
            this.buyUSDT(amount, value, isBuyStake, isUsdc);
        }
    }

    /**
     * this function is swaping the usdt token with app token
     * @param amount: app token amount
     * @param value: usdt token value
     * @param afterAllowance: checking whether allowence is granted or not
     */

    async buyUSDT(amount: number, value: number, isBuyStake: boolean = false, isUsdc: boolean = false) {
        /**
         * preparing a contract for swaping via smart contract
         * method of buyWithUSDT
         */
        const referralAddress = getParam('referralAddress') || "0x0000000000000000000000000000000000000000";
        const isValidReferral = await checkIsValidReferral(referralAddress, this.dataService.chainId, this.dataService.walletAddress, this.dataService.wagmiConfig);
        const referral = isValidReferral ? referralAddress : "0x0000000000000000000000000000000000000000";

        let functionName = isUsdc ? 'buyWithUSDC' : 'buyWithUSDT';
        const contract = getPresaleContract(this.dataService.chainId);
        await simulateContract(this.dataService.wagmiConfig, {
            ...contract,
            functionName: functionName,
            chainId: this.dataService.chainId,
            args: [amount, referral],
            // account: this.walletAddress as Address   // Optional param. Not required!
        })
            // if prepare contract is success
            .then(
                async (request) => {
                    // setting swapStatus to confirmation pending
                    console.log('usdtBuyHelper =', request);
                    this.$transactionUpdates.next({ status: SwapStatus.confirm_pending })
                    this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: true, walletTransactionStatus: '', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                    const isEthChain = this.dataService.chainId === environment.eth.chainIdInt;

                    /**
                     * Once confirmed startig write contract
                     */
                    await writeContract(this.dataService.wagmiConfig, request.request).then(
                        (hash: WriteContractReturnType) => {
                            console.log('USDT writeContract success', hash);
                            this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'approved', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: hash });
                            this.analyticsService.windowDataLayer('swap', 'confirmTransaction', 2, 0, 0, '', '', 0, 0, this.dataService.walletAddress, hash, this.dataService.balanceDataEVM, this.dataService.chainId, this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep]?.tokenUSDT); // setting windowdatalayer for analytics;
                            this.$transactionUpdates.next({ status: SwapStatus.in_progess, hash: hash })
                            this.checkTransaction(hash, isUsdc ? 'usdc' : 'usdt', amount, value, isBuyStake ? 'buy_and_stake' : 'buy', isEthChain ? 'eth_usdt' : 'bnb_usdt'); // checking transaction status
                        },
                        (error: WriteContractErrorType) => {
                            console.log('USDT writeContract error', error);
                            this.$transactionUpdates.next({ status: SwapStatus.rejected })
                            this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                            this.vwoData('failedAndRejectTransaction', {
                                transactionStatus: 'rejected',
                                fundSpentAmount: value,
                                tokenAmountPurchase: amount,
                                purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                                tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                                transactionId: '',
                                walletAddress: this.dataService.walletAddress,
                                walletType: this.dataService.connectedWalletName,
                            });
                        },
                    );
                },

                /**
                 * if there is any error while preparing contract
                 * like requesting more than max-token buy limit
                 * then a warning popup will appear saying that
                 * the max-token limitation
                 */
                (err: any) => {
                    this.popupService.messagePopup({ icon: 'warning', title: this.transletService.instant('widget.max_token_title'), desc: this.transletService.instant('widget.max_token_exceed', { maxTokensToBuy: this.dataService.balanceDataEVM.maxTokensToBuyEVM }) });
                    this.$transactionUpdates.next({ status: SwapStatus.rejected })
                    this.vwoData('walletTransactionsStatus', { walletTransactionPrompted: false, walletTransactionStatus: 'rejected', walletTransactionWalletAddress: this.dataService.walletAddress, walletTransactionHash: '' });
                    this.vwoData('failedAndRejectTransaction', {
                        transactionStatus: 'rejected',
                        fundSpentAmount: value,
                        tokenAmountPurchase: amount,
                        purchaseType: isBuyStake ? 'buy_and_stake' : 'buy',
                        tokenPurchaseMethod: this.dataService.chainId === environment.eth.chainIdInt ? 'eth' : 'bsc',
                        transactionId: '',
                        walletAddress: this.dataService.walletAddress,
                        walletType: this.dataService.connectedWalletName,
                    });
                    console.log(err.message);
                },
            );
    }


    vwoData(eventName: string, eventParams: any, hasParam = true) {
        this.analyticsService.vwoData(eventName, eventParams, hasParam);
    }


    /**
 * Function to buy app token via wert widget.
 * this function convert the USDT value to app token
 * @param amount: Amount to spend
 * @returns : converted no of app tokens
 */

    getDynamicAmountWertEth = async (amount: number) => {
        console.log('getDynamicAmountWert', amount);
        const _wagmiConfig = this.dataService.wagmiConfig;
        const contractData = await readContracts(_wagmiConfig, {
            contracts: [
                {
                    ...presaleContractEth,
                    functionName: 'ethBuyHelper',
                    chainId: environment.eth.chainIdInt,
                    args: [amount],
                },
            ],
        });
        let ethVal = +formatEther(contractData[0].result as any);
        console.log('return DynamicAmountWert', ethVal);
        return +ethVal;
    };


    getBonusAmount = async (amount: number) => {
        console.log('getBonusAmount', amount);
        const _wagmiConfig = this.dataService.wagmiConfig;
        const contractData = await readContracts(_wagmiConfig, {
            contracts: [
                {
                    ...presaleContractEth,
                    functionName: 'getBonusAmount',
                    chainId: environment.eth.chainIdInt,
                    args: [amount],
                },
            ],
        });
        let bonusValue = Number(contractData[0].result as any);
        console.log('Bonus value from contract is', bonusValue)
        return +bonusValue;
    };


    async claimToken() {
        console.log('Claim called');
        this.$transactionUpdates.next({ status: SwapStatus.in_progess })
        const { request } = await simulateContract(this.dataService.wagmiConfig, {
            ...presaleContractEth,
            functionName: 'claim',
            chainId: environment.eth.chainIdInt,
            args: [],
            account: this.dataService.walletAddress as Address, // Optional param. Not required!
        });

        await writeContract(this.dataService.wagmiConfig, request).then(
            (hash: any) => {
                console.log('Claim success', hash);
                this.$transactionUpdates.next({ status: SwapStatus.complete, hash: hash })
            },
            (err: any) => {
                console.log('Claim error', err);
                this.$transactionUpdates.next({ status: SwapStatus.failed, hash: '' })
            },
        );
    }
}
